import { functionalUpdate } from '@tanstack/react-table';
import {
  type ReactElement,
  type ReactNode,
  type PropsWithChildren,
  Children,
} from 'react';
import { isElement } from 'react-is';

import { getPaginationRowModel } from './pagination-row-model.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { DEFAULT_PAGE_SIZE_OPTION } from '../../../DataTableV2/constants.js';
import { useDataTableV2ConfigElement } from '../../../hooks/useDataTableV2Config.js';
import type {
  RowModel,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * @public
 */
export interface DataTableV2PaginationBaseProps {
  /**
   * The element's unique identifier. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id).
   */
  id?: string;

  /**
   * Test id used for matching the pagination container.
   */
  'data-testid'?: string;

  /**
   * Page size options to be displayed in the page size select.
   * @defaultValue [10, 20, 50, 100, 250, 500, 1000]
   */
  pageSizeOptions?: number[];

  /**
   * Allows opting out of automatic page index reset when new data is being set.
   * Be aware that if you turn off `autoResetPageIndex`, you may need to add logic
   * to handle resetting the pageIndex yourself.
   *
   * @remarks If the page index is resetting when you think it should not be, before
   * using this setting, check that you have your table input objects properly
   * memoized (for example rowSelection object).
   * @defaultValue true
   */
  autoResetPageIndex?: boolean;

  /** Called when the page index changes. Provides the changed page index along with the underlying trigger. */
  onPageIndexChange?: (pageIndex: number, trigger: 'user' | 'internal') => void;

  /** Handler that is called when the page size changes. */
  onPageSizeChange?: (pageSize: number) => void;
}

/**
 * @public
 */
export interface DataTableV2PaginationPageIndexUncontrolledProps
  extends DataTableV2PaginationBaseProps {
  /**
   * Initial page index in an uncontrolled scenario.
   * @defaultValue 0
   */
  defaultPageIndex?: number;
  /** Current page index in a controlled scenario. */
  pageIndex?: never;
}

/**
 * @public
 */
export interface DataTableV2PaginationPageIndexControlledProps
  extends DataTableV2PaginationBaseProps {
  /**
   * Initial page index in an uncontrolled scenario.
   * @defaultValue 0
   */
  defaultPageIndex?: never;
  /** Current page index in a controlled scenario. */
  pageIndex: number;
}

/**
 * @public
 */
export interface DataTableV2PaginationPageSizeUncontrolledProps
  extends DataTableV2PaginationBaseProps {
  /**
   * Default number of rows per page (uncontrolled).
   * @defaultValue 100
   */
  defaultPageSize?: number;
  /** Number of rows per page (controlled) */
  pageSize?: never;
}
/**
 * @public
 */
export interface DataTableV2PaginationPageSizeControlledProps
  extends DataTableV2PaginationBaseProps {
  /**
   * Default number of rows per page (uncontrolled).
   * @defaultValue 100
   */
  defaultPageSize?: never;
  /** Number of rows per page (controlled) */
  pageSize: number;
}

/**
 * @public
 */
export interface DataTableV2PaginationClientSideProps
  extends DataTableV2PaginationBaseProps {
  /** Whether the previous page is available (if not, the go to previous page button should be disabled).	*/
  enablePreviousPage?: never;
  /** Whether the next page is available (if not, the go to next page button should be disabled). */
  enableNextPage?: never;
  /**
   * The total number of rows.
   * Will be used to determine the total number of pages to display the indicator "Page X of Y" for server-side pagination.
   */
  totalRowsCount?: never;
}
/**
 * @public
 */
export interface DataTableV2PaginationServerSideProps
  extends DataTableV2PaginationBaseProps {
  /** Whether the previous page is available (if not, the go to previous page button should be disabled).	*/
  enablePreviousPage: boolean;
  /** Whether the next page is available (if not, the go to next page button should be disabled). */
  enableNextPage: boolean;
  /**
   * The total number of rows.
   * Will be used to determine the total number of pages to display the indicator "Page X of Y" for server-side pagination.
   */
  totalRowsCount?: number;
}

/**
 * DataTableV2.Pagination props.
 * @public
 */
export type DataTableV2PaginationProps = (
  | DataTableV2PaginationPageIndexUncontrolledProps
  | DataTableV2PaginationPageIndexControlledProps
) &
  (
    | DataTableV2PaginationPageSizeUncontrolledProps
    | DataTableV2PaginationPageSizeControlledProps
  ) &
  (DataTableV2PaginationClientSideProps | DataTableV2PaginationServerSideProps);

/**
 * DataTableV2 slot definition.
 * @public
 */
export function DataTableV2Pagination(props: DataTableV2PaginationProps) {
  return null;
}

/**
 * Helper function that will check if a passed component is of type `DataTableV2Pagination`
 * @internal
 */
export function _isDataTableV2Pagination(
  child: ReactNode,
): child is ReactElement<DataTableV2PaginationProps> {
  if (isElement(child) && child.type === DataTableV2Pagination) {
    return true;
  }
  return false;
}

//#region React table extension interfaces

/** @internal */
export interface DataTableV2PaginationState {
  pageIndex: number;
  pageSize: number;
  enableNextPage?: boolean;
  enablePreviousPage?: boolean;
  totalRowsCount?: number;
}

/** @internal */
export interface DataTableV2PaginationOptions<TData> {
  enablePagination?: boolean;

  availablePaginationOptions?: number[];

  autoResetPageIndex?: boolean;

  getPaginationRowModel?: (table: Table<TData>) => () => RowModel<TData>;

  manualPagination?: boolean;

  onPaginationChange?: (
    updaterOrValue: Updater<DataTableV2PaginationState>,
    trigger?: 'user' | 'internal',
  ) => void;

  pageCount?: number;

  /**
   * Whether the page index is controlled.
   */
  controlledPageIndex?: boolean;
}

/** @internal */
export interface DataTableV2PaginationInitialTableState {
  pagination?: Partial<DataTableV2PaginationState>;
}

/** @internal */
export interface DataTableV2PaginationTableState {
  pagination: DataTableV2PaginationState;
}

/** @internal */
export interface DataTableV2PaginationInstance {
  /**
   * Updates the page index using the provided function or value.
   * @param updater - update function or value
   * @param trigger - whether the change was triggered by user interaction or internally
   */
  setPageIndex: (
    updater: Updater<number>,
    trigger?: 'user' | 'internal',
  ) => void;

  /**
   * Sets or updates the pagination state.
   * @param updater - update function or value
   * @param trigger - whether the change was triggered by user interaction or internally
   */
  setPagination: (
    updater: Updater<DataTableV2PaginationState>,
    trigger?: 'user' | 'internal',
  ) => void;

  /**
   * For uncontrolled pagination, if there are no rows to display on the current page,
   * the page index is set to the last non-empty page.
   */
  updatePageIndexOnEmptyPage: () => void;
}
//#endregion

const DATATABLEV2_PAGINATION_DEFAULTS = {
  pageIndex: 0,
  pageSizeOptions: [10, 20, 50, 100, 250, 500, 1000],
};

/**
 * Returns the default page size based on `defaultPageSize` being
 * passed as a prop, or the first option of `pageSizeOptions` array
 * if `defaultPageSize` is passed.
 * @internal
 * @defaultValue 100
 */
function getDefaultPageSize(
  pageSizeOptions?: number[],
  defaultPageSize?: number,
): number {
  if (typeof defaultPageSize === 'number') {
    return defaultPageSize;
  }

  if (Array.isArray(pageSizeOptions) && pageSizeOptions[0]) {
    return pageSizeOptions[0];
  }

  return DEFAULT_PAGE_SIZE_OPTION;
}

/**
 * @internal
 */
export const DataTableV2PaginationFeature: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    table.setPagination = (updater, trigger) => {
      const safeUpdater: Updater<DataTableV2PaginationState> = (old) => {
        const newState = functionalUpdate(updater, old);

        return newState;
      };

      return table.options.onPaginationChange?.(safeUpdater, trigger);
    };

    table.setPageIndex = (updater, trigger) => {
      table.setPagination((old) => {
        let pageIndex = functionalUpdate(updater, old.pageIndex);

        const maxPageIndex =
          typeof table.options.pageCount === 'undefined' ||
          table.options.pageCount === -1
            ? Number.MAX_SAFE_INTEGER
            : table.options.pageCount - 1;

        pageIndex = Math.max(0, Math.min(pageIndex, maxPageIndex));

        return {
          ...old,
          pageIndex,
        };
      }, trigger);
    };

    table.updatePageIndexOnEmptyPage = () => {
      if (
        table.options.enablePagination === false ||
        table.options.controlledPageIndex
      ) {
        return;
      }

      const { pageIndex, pageSize } = table.getState().pagination;
      const currentPageRowCount = table.getPaginationRowModel().rows.length;

      // If there are no rows to display on the current page, navigate to the last non-empty page
      if (pageIndex > 0 && currentPageRowCount === 0) {
        const rowCount = table.getCoreRowModel().rows.length;
        const firstNonEmptyPrevPage = Math.ceil(rowCount / pageSize) - 1;
        table.setPageIndex(firstNonEmptyPrevPage, 'internal');
      }
    };

    table.resetPageIndex = (defaultState) => {
      table.setPageIndex(
        defaultState
          ? DATATABLEV2_PAGINATION_DEFAULTS.pageIndex
          : table.initialState?.pagination?.pageIndex ??
              DATATABLEV2_PAGINATION_DEFAULTS.pageIndex,
        'internal',
      );
    };

    table.getPrePaginationRowModel = () => table.getRowOrderRowModel();
  },
};

/**
 * Configuration hook for pagination of the DataTableV2.
 * @internal
 */
export function usePagination<TData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
) {
  const configPagination = useDataTableV2ConfigElement('pagination');

  const paginationElement = Children.toArray(props.children).find(
    _isDataTableV2Pagination,
  );

  // Init the paginationOptions to make sure they are a valid boolean.
  options.enablePagination = false;

  const paginationSizeOptions =
    paginationElement?.props.pageSizeOptions ??
    DATATABLEV2_PAGINATION_DEFAULTS.pageSizeOptions;

  const defaultPaginationSizeOption = getDefaultPageSize(
    paginationElement?.props.pageSizeOptions,
    paginationElement?.props.defaultPageSize ??
      configPagination?.defaultPageSize,
  );

  const [pageSize, setPageSize] = useControllableState({
    value: paginationElement?.props.pageSize,
    defaultValue: defaultPaginationSizeOption,
    onChange: paginationElement?.props.onPageSizeChange,
  });

  const defaultPaginationIndexOption =
    paginationElement?.props.defaultPageIndex ??
    configPagination?.defaultPageIndex ??
    DATATABLEV2_PAGINATION_DEFAULTS.pageIndex;

  const [pageIndex, setPageIndex] = useControllableState({
    value: paginationElement?.props.pageIndex,
    defaultValue: defaultPaginationIndexOption,
  });

  if (!paginationElement) {
    return;
  }

  options.enablePagination = true;
  options.availablePaginationOptions = paginationSizeOptions;
  options.controlledPageIndex = paginationElement.props.pageIndex !== undefined;

  if (paginationElement.props.autoResetPageIndex === false) {
    options.autoResetPageIndex = paginationElement.props.autoResetPageIndex;
  }

  // Server side pagination.
  if (
    paginationElement.props.enableNextPage !== undefined &&
    paginationElement.props.enablePreviousPage !== undefined
  ) {
    options.manualPagination = true;
    // Ensure pageCount is considered unknown to react table.
    options.pageCount = -1;
  } else {
    options.manualPagination = false;
    options.getPaginationRowModel = getPaginationRowModel();
  }
  options.state = {
    ...options.state,
    pagination: {
      pageSize,
      pageIndex,
      enablePreviousPage: paginationElement.props.enablePreviousPage,
      enableNextPage: paginationElement.props.enableNextPage,
      totalRowsCount: paginationElement.props.totalRowsCount,
    },
  };

  options.onPaginationChange = (
    updater: Updater<DataTableV2PaginationState>,
    trigger: 'user' | 'internal' = 'user',
  ) => {
    const nextState = functionalUpdate(
      updater,
      options.state?.pagination ?? {
        pageSize,
        pageIndex,
      },
    );

    if (nextState?.pageSize !== pageSize && nextState?.pageSize !== undefined) {
      setPageSize(nextState.pageSize);
      // Reset page index once page size changes
      setPageIndex(DATATABLEV2_PAGINATION_DEFAULTS.pageIndex);
      paginationElement?.props.onPageIndexChange?.(
        DATATABLEV2_PAGINATION_DEFAULTS.pageIndex,
        'internal',
      );
    } else if (
      nextState?.pageIndex !== pageIndex &&
      nextState?.pageIndex !== undefined
    ) {
      setPageIndex(nextState.pageIndex);
      paginationElement?.props.onPageIndexChange?.(
        nextState.pageIndex,
        trigger,
      );
    }
  };
}
